import { addToast } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Col, Container, Row } from 'reactstrap';
import GoBackButton from '../../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../../config/routes';
import { PeriodType } from '../../../../../types/PeriodType';
import { getCurriculumRequest } from '../../api/study-plan-curriculums';
import CurriculumForm from '../CurriculumForm';
import { CurriculumLegend } from '../CurriculumLegend';
import CurriculumStudyPlanInfo from '../CurriculumStudyPlanInfo';
import { CurriculumDegreeActivitiesList } from '../parts/CurriculumDegreeActivities';
import { DegreeAverageOptions } from '../parts/DegreeAverageOptions';
import { DegreeDocuments } from '../parts/DegreeDocuments';
import { CURRICULUM_STATUS } from '../../../../../types/CurriculumTypes';

type PropsParams = {
  id: string;
  versionId: string;
  curriculumId: string;
};

const CurriculumEdit = () => {
  const prefix = 'studyPlans.curriculum';

  const history = useHistory();
  const { t } = useTranslation();
  const { id: studyPlanId, versionId, curriculumId } = useParams<PropsParams>();

  const [periodType, setPeriodType] = useState<PeriodType>();
  const [studyPlanName, setStudyPlanName] = useState<string>('');
  const [curriculumData, setCurriculumData] = useState<any>(undefined);
  const [disabledEditButton, setDisabledEditButton] = useState<boolean>(false);
  // HU 15366 — la malla solo es editable mientras esté en borrador.
  const readOnly =
    !curriculumData ||
    curriculumData?.status?.toLowerCase() !== CURRICULUM_STATUS.borrador;

  const goBack = useCallback(() => {
    history.push(
      `${PathsLayouts.academicOffers}/study-plans/record/${studyPlanId}`,
    );
  }, [history, studyPlanId]);

  const getCurriculumData = useCallback(
    async (id: string) => {
      const { data, error } = await getCurriculumRequest(id);
      if (data) {
        setCurriculumData(data.data);
      }
      if (error) {
        addToast({
          text: t(`${prefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      }
    },
    [t],
  );

  useEffect(() => {
    if (curriculumId) {
      getCurriculumData(curriculumId);
      setDisabledEditButton(true);
    }
  }, [curriculumId, getCurriculumData]);

  return (
    <Container>
      <Card className="p-3 p-md-4">
        <Row>
          <Col xs={12} md={7}>
            <div className="mb-4">
              <GoBackButton onClick={goBack} />
            </div>
          </Col>
          <Col xs={12} md={5}>
            <CurriculumStudyPlanInfo
              id={studyPlanId}
              versionId={versionId}
              setStudyPlanName={setStudyPlanName}
              setPeriodType={setPeriodType}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={12}>
            <CurriculumForm
              studyPlanName={studyPlanName}
              curriculumData={curriculumData}
              periodType={periodType}
              readOnly={readOnly}
              setDisabledEditButton={setDisabledEditButton}
              isMention={false}
              showSubmitButton={!!curriculumData && !readOnly}
              disabledSubmitButton={disabledEditButton || readOnly}
              prefix={prefix}
            >
              {({ legend }) => (
                <>
                  <Row>
                    <Col xs={{ size: 12 }}>
                      <CurriculumLegend legend={legend} />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={{ size: 12 }}>
                      {
                        <DegreeAverageOptions
                          readOnly={readOnly}
                          initialGraduationPercentage={
                            curriculumData?.graduationPercentageForDegreeAverage
                          }
                        />
                      }
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={{ size: 12 }}>
                      {
                        <CurriculumDegreeActivitiesList
                          readOnly={readOnly}
                          curriculumId={curriculumId}
                          studyPlanId={studyPlanId}
                        />
                      }
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={{ size: 12 }}>
                      <DegreeDocuments
                        degreeDocuments={curriculumData?.degreeDocuments}
                        readOnly={readOnly}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </CurriculumForm>
          </Col>
        </Row>
      </Card>
    </Container>
  );
};

export default CurriculumEdit;
